import { MouseEvent, useEffect, useMemo, useState, Dispatch, SetStateAction } from "react"
import PopupHeader from "../../../components/Popups/PopupHeader"
import PopupSideNav from "../../../components/Popups/PopupSideNav";
import PopupFooter from "../../../components/Popups/PopupFooter";
import Buttons, { Btn } from "../../../components/Buttons/Buttons";
import SetDatasetParameters from "./GenerateDSComponents/SetDatasetParameters";
import { DataFile, EcleanCurationDAGRequestModel } from "../../../api/models";

import { ReactComponent as PlusIcon } from '../../../assets/Icons/adornments/Plus.svg';
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import SpecifyColumnMetadata from "./GenerateDSComponents/SpecifyColumnMetadata";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import Curate, { CurationTypes, EcleanCuration } from "./GenerateDSComponents/Curate";
import { SelectListOption } from "../../../components/Filtering/PaginatedSelectList";
import SelectFile from "./GenerateDSComponents/SelectFile";
import { customInstance } from "../../../api/custom-instance";

const navItems = ['Select File', 'Set Dataset Parameters', 'Specify Column Metadata', 'Curate Dataset'];

export type DatasetCurationTypes = 'mixed' | 'non-binary' | 'binary' | 'smiles-only';

export interface SpecifyColumnMetadataType {
    activityColumns: {
        assignedColumn: string;
        data_type: 'single-class-label' | 'continuous-value' | '',
        description: string;
    }[];
    chemicalName: {
        assignedColumn: string;
        description: string;
    };
    structure: {
        assignedColumn: string;
        description: string;
    };
    recordId: {
        assignedColumn: string;
        description: string;
    };
    other: {
        assignedColumn: string;
        description: string;
    }[];
}


const defaultColumnMetadata: SpecifyColumnMetadataType = {
    activityColumns: [
        {
            assignedColumn: '',
            data_type: '',
            description: '',
        },
    ],
    chemicalName: {
        assignedColumn: '',
        description: '',
    },
    structure: {
        assignedColumn: '',
        description: '',
    },
    recordId: {
        assignedColumn: '',
        description: '',
    },
    other: [
        {
            assignedColumn: '',
            description: '',
        }
    ],
}



const defaultDatasetForm: EcleanCurationDAGRequestModel = {
    data_file_hash: '',
    name: '',
    created_by: '',
    owner: '',
    tag_behavior: 'define',
    tags: [],
    stages: [],
}

const defaultUpdatedCurationForm: EcleanCuration = {
    eclean_std_smiles: {
        original_smiles_column: '',
        remove_stereo: false,
    },
    eclean_std_binary_values: null,
    eclean_std_values: null,
    eclean_std_binary: null,
    eclean_std_regression: null,
    input_units: null,
}



export default function GenerateDatasetModal({
    selectedFileId,
    setPopupMessage,
    isDropdownItem,
    handleCloseMenu,
    refetchDatasets,
    open: openProp,
    setOpen: setOpenProp,
}: {
    selectedFileId?: string,
    setPopupMessage : (popupMessage: string, isSuccessMessage: boolean) => void,
    isDropdownItem: boolean,
    handleCloseMenu?: () => void,
    refetchDatasets?: () => void,
    open?: boolean,
    setOpen?: Dispatch<SetStateAction<boolean>>,
}) {
    const [openBackup, setOpenBackup] = useState(false);
    // Use prop if provided, otherwise fallback to internal state
    const open = openProp !== undefined ? openProp : openBackup;
    const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenBackup;
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);


    const [ selectedNavIndex, setSelectedNavIndex ] = useState(0);
    const [ selectedFiles, setSelectedFiles ] = useState<DataFile[]>([]);

    const [ dsForm, setDsForm ] = useState<EcleanCurationDAGRequestModel>(defaultDatasetForm);
    const [ curationForm, setCurationForm ] = useState<EcleanCuration>(defaultUpdatedCurationForm);
    const [ columnMetadataForm, setColumnMetadataForm ] = useState<SpecifyColumnMetadataType>(defaultColumnMetadata);
    const [ isSDF, setIsSDF ] = useState<boolean>(false);
    const [ isCurationTrue, setIsCurationTrue ] = useState(false);
    const [ curationType, setCurationType ] = useState<CurationTypes>('smiles');

    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isPrefetchLoading, setIsPrefetchLoading ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);
    const [ formBtns, setFormBtns ] = useState<Btn[]>([]);

    const resetValues = () => {
        setSelectedNavIndex(0);
        setSelectedFiles([]);
        setDsForm(defaultDatasetForm);
        setCurationForm(defaultUpdatedCurationForm);
        setIsCurationTrue(false);
        setIsDisabled(false);
        setIsLoading(false);
        setIsPrefetchLoading(false);
        setIsSuccess(false);
        setColumnMetadataForm(defaultColumnMetadata);

        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    }

    const handleClose = () => {
        setOpen(false);
        resetValues();
        setShowSelect(false);
    };

    const { handleGenerateDataset } = usePost({
        setIsLoading,
        setIsDisabled,
        setIsSuccess,
        setPopupMessage,
        refetchFn: refetchDatasets,
    })


    // If selected file, set as chosen file and navigate to second tab by default
    useEffect(() => {
        const fetchFile = async (id: string) => {
            setIsPrefetchLoading(true);

            try {
                const response = await customInstance({
                    url: `data_file/?data_file_hash=${id}`,
                    method: 'GET',
                });

                const data = response as unknown as DataFile;
                setSelectedFiles([data]);
            } catch (err:any) {
                console.log(err);
            };

            setIsPrefetchLoading(false);
        }

        if (selectedFileId) {
            setSelectedNavIndex(0);
            fetchFile(selectedFileId);
        }
    }, [selectedFileId]);

    // Sets default tags and field options based on chosen file

    const default_smiles_column_name = 'sdf_molblock';

    useEffect(() => {
        if (selectedFiles.length > 0) {
            const tags: string[] = [];
            const selectedFile = selectedFiles[0];
            const name = selectedFile.name ?? '';

            const fileTags = selectedFile.file_metadata && selectedFile.file_metadata.tags ? selectedFile.file_metadata.tags as string[] : [];
            fileTags.forEach(tag => {
                const tagValue = tag ?? 'null';
                if (!tags.includes(tagValue)) {
                    tags.push(tagValue);
                }
            });

            let original_smiles_column = '';
            if (selectedFile.file_extension === '.sdf') {
                original_smiles_column = default_smiles_column_name;
                setIsSDF(true);
            } else {
                setIsSDF(false);
            }

            setCurationForm({
                ...defaultUpdatedCurationForm,
                eclean_std_smiles: {
                    original_smiles_column,
                    remove_stereo: false,
                }
            })

            setColumnMetadataForm({
                ...defaultColumnMetadata,
                structure: {
                    ...defaultColumnMetadata.structure,
                    assignedColumn: original_smiles_column,
                }
            });

            setDsForm((prevForm) => ({
                ...prevForm,
                name: name.split('.')[0],
                data_file_hash: selectedFile.file_hash!,
                created_by: selectedFile.created_by!,
                owner: selectedFile.owner!,
                tags,
            }))
        }
    }, [selectedFiles]);

    const activityColumns: {
        column: string,
        data_type: string,
    }[] = useMemo(() => {
        if (columnMetadataForm.activityColumns.length === 0) return [];

        const activity: { column: string, data_type: string }[] = [];

        columnMetadataForm.activityColumns.forEach(col => {
            const { data_type, assignedColumn } = col;
            if (data_type && assignedColumn) {
                activity.push({ column: assignedColumn, data_type });
            }
        });

        return activity;

    }, [columnMetadataForm]);

    const fieldOptions: { value: string, label: string }[] = useMemo(() => {
        if (selectedFiles.length === 0) return [];

        let options: { value: string, label: string }[] = [];

        const selectedFile = selectedFiles[0];

        const columnMetadata = selectedFile.datafile_metadata;
        if (columnMetadata && columnMetadata.fields) {
            options = columnMetadata.fields.map(field => ({ value: field, label: field }));
        }

        return options;
    }, [selectedFiles]);


    useEffect(() => {
        const chosenDatafile: SelectListOption | null = selectedFiles.length > 0 ? { id: selectedFiles[0].file_hash!, name: selectedFiles[0].name ?? '' } : null;

        setFormBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-ds-generation',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'generate-ds-btn',
                label: "Run",
                action: async (e: MouseEvent<HTMLButtonElement>) => {chosenDatafile && handleGenerateDataset(e, isCurationTrue, curationType, dsForm, curationForm, columnMetadataForm)},
                isLoading,
            }
        ])

        //eslint-disable-next-line
    }, [isDisabled, isLoading, isCurationTrue, dsForm, curationForm, selectedFiles, columnMetadataForm, curationType]);

    

    const checkIfLocked = (index: number) => {
        if (navItems[index] !== 'Select File') {
            if (selectedFiles.length > 0) {
                if (navItems[index] === 'Curate Dataset' && !columnMetadataForm.structure.assignedColumn) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    const checkIfComplete = (index: number) => {
        if (selectedFiles.length > 0) {
            if (navItems[index] === 'Select File') {
                return true;
            } else if (navItems[index] === 'Set Dataset Parameters') {
                if (dsForm.name) {
                    return true;
                } else {
                    return false;
                }
            } else if (navItems[index] === 'Specify Column Metadata' && columnMetadataForm.structure.assignedColumn) {
                return true
            } else {
                let complete = true;

                if (!isCurationTrue) {
                    complete = false;
                }

                const { 
                    eclean_std_smiles,
                    eclean_std_binary_values,  
                    eclean_std_values,
                    eclean_std_binary, 
                    eclean_std_regression,
                    input_units
                } = curationForm;

                if (eclean_std_smiles && !eclean_std_smiles.original_smiles_column) {
                    complete = false;
                }

                if (eclean_std_binary_values !== null) {
                    if (!eclean_std_binary_values.binary_value_column) {
                        complete = false;
                    }
                }

                if (eclean_std_values !== null) {
                    if (!eclean_std_values.units_out || !eclean_std_values.value_column) {
                        complete = false;
                    } else if (!eclean_std_values.units_column && !input_units) {
                        complete = false;
                    }
                }

                if (eclean_std_binary !== null) {
                    if (eclean_std_binary.binarization_threshold === null) {
                        if (!eclean_std_binary.binary_value_column) {
                            complete = false;
                        }
                    } else {
                        if (!eclean_std_binary.binarization_threshold_unit_conversion!.binarization_units || !eclean_std_binary.binarization_threshold_unit_conversion!.units_out || !eclean_std_binary.value_column) {
                            complete = false;
                        }
                    }
                } 

                if (eclean_std_regression !== null) {
                    if (!eclean_std_regression.value_column) {
                        complete = false;
                    }
                };

                return complete;
            }
        } else {
            return false;
        }
    }

    useEffect(() => {
        let disabled = false;

        if (selectedFiles.length === 0) {
            disabled = true;
        } else {
            if (!dsForm.name || !columnMetadataForm.structure.assignedColumn) {
                disabled = true;
            } else if (isCurationTrue) {
                const { 
                    eclean_std_smiles,
                    eclean_std_binary_values,  
                    eclean_std_values,
                    eclean_std_binary, 
                    eclean_std_regression,
                    input_units
                } = curationForm;

                if (eclean_std_smiles && !eclean_std_smiles.original_smiles_column) {
                    disabled = true;
                }

                if (eclean_std_binary_values !== null) {
                    if (!eclean_std_binary_values.binary_value_column) {
                        disabled = true;
                    }
                }

                if (eclean_std_values !== null) {
                    if (!eclean_std_values.units_out || !eclean_std_values.value_column) {
                        disabled = true;
                    } else if (!eclean_std_values.units_column && !input_units) {
                        disabled = true;
                    }
                }

                if (eclean_std_binary !== null) {
                    if (eclean_std_binary.binarization_threshold === null) {
                        if (!eclean_std_binary.binary_value_column) {
                            disabled = true;
                        }
                    } else {
                        if (!eclean_std_binary.binarization_threshold_unit_conversion!.binarization_units || !eclean_std_binary.binarization_threshold_unit_conversion!.units_out || !eclean_std_binary.value_column) {
                            disabled = true;
                        }
                    }
                } 

                if (eclean_std_regression !== null) {
                    if (!eclean_std_regression.value_column) {
                        disabled = true;
                    }
                }
            }
        }

        setIsDisabled(disabled);

    }, [selectedFiles, dsForm.name, columnMetadataForm, curationForm, isCurationTrue]);




    return (
        <>
            {isDropdownItem ? (
                null
            ) : (
                <Buttons
                    id='generate-ds-btn'
                    type='primary'
                    styling="solid"
                    label="Generate Dataset"
                    startingAdornment={<PlusIcon/>}
                    action={handleOpen}
                />
            )}

            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="generate-ds-popup"
            >
            {!isSuccess ? (
                <div className={`bg-white rounded-lg w-full max-w-[900px] popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Generate Dataset"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full flex justify-center items-stretch overflow-y-auto">
                            <PopupSideNav 
                                items={navItems.map((item, i) => ({
                                    label: item,
                                    type: 'number',
                                    isDisabled: checkIfLocked(i),
                                    isActive: selectedNavIndex === i ? true : false,
                                    isComplete: checkIfComplete(i),
                                    action: (e: MouseEvent<HTMLButtonElement>) => {setSelectedNavIndex(i)},
                                }))}
                                selectedIndex={selectedNavIndex}
                            />
                            <div className="w-full grow overflow-y-auto border-l border-light-outline">
                                {navItems.map((navItem, index) => (
                                    <div className={`w-full p-6 ${index === selectedNavIndex ? 'flex' : 'hidden'} flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                        {navItem === 'Select File' ? (
                                            <SelectFile 
                                                preselectedFileId={selectedFileId}
                                                selectedFiles={selectedFiles}
                                                setSelectedFiles={setSelectedFiles}
                                                isPrefetchLoading={isPrefetchLoading}
                                            />
                                        ) : navItem === 'Set Dataset Parameters' ? (
                                            <SetDatasetParameters 
                                                form={dsForm}
                                                setForm={setDsForm}
                                                chosenDatafile={selectedFiles.length > 0 ? selectedFiles[0] : null}
                                                showSelect={showSelect}
                                            />
                                        ) : navItem === 'Specify Column Metadata' ? (
                                            <SpecifyColumnMetadata 
                                                columnMetadataForm={columnMetadataForm}
                                                setColumnMetadataForm={setColumnMetadataForm}
                                                fieldOptions={fieldOptions}
                                                showSelect={showSelect}
                                                isSDF={isSDF}
                                            />
                                        ) : (
                                            <Curate 
                                                isCurationTrue={isCurationTrue}
                                                setIsCurationTrue={setIsCurationTrue}
                                                curationType={curationType}
                                                setCurationType={setCurationType}
                                                fieldOptions={fieldOptions}
                                                form={curationForm}
                                                setForm={setCurationForm}
                                                activityColumns={activityColumns}
                                                stdSMILES={null}
                                                originalSMILES={!isSDF ? '' : default_smiles_column_name}
                                                menuPortalTargetId="generate-ds-popup"
                                                showSelect={showSelect}
                                                isSDF={isSDF}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={formBtns}
                        />
                    </div>
                </div>
            ): (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your dataset is being generated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Datasets page."
                />
            )}
            </ModalBase>
        </>
    )
}